import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { gastosApi } from '../api/gastos';
import { normalizePaginatedResponse } from '../lib/pagination';
import { Gasto } from '../types/gasto';
import { toast } from 'sonner';

interface UseGastosParams {
  search?: string;
  categoria?: string;
  fechaInicio?: string;
  fechaFin?: string;
  page?: number;
  limit?: number;
}

export function useGastos(params?: UseGastosParams) {
  return useQuery({
    queryKey: [
      'gastos',
      params?.search,
      params?.categoria,
      params?.fechaInicio,
      params?.fechaFin,
      params?.page,
      params?.limit,
    ],
    queryFn: async () => {
      const response = await gastosApi.getAll(params);
      return normalizePaginatedResponse<Gasto>(response);
    },
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });
}

export function useGasto(id: number) {
  return useQuery({
    queryKey: ['gastos', id],
    queryFn: () => gastosApi.getById(id),
    enabled: !!id && id > 0,
    staleTime: 1000 * 60 * 2,
  });
}

export function useGastosStats() {
  return useQuery({
    queryKey: ['gastos', 'stats'],
    queryFn: () => gastosApi.getStats(),
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 30,
  });
}

export function useGastosMensuales(meses: number = 6) {
  return useQuery({
    queryKey: ['gastos', 'mensuales', meses],
    queryFn: () => gastosApi.getMensuales(meses),
    staleTime: 1000 * 60 * 5,
  });
}

export function useGastosDistribucion(meses: number = 1) {
  return useQuery({
    queryKey: ['gastos', 'distribucion', meses],
    queryFn: () => gastosApi.getDistribucion(meses),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateGasto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => gastosApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gastos'] });
      queryClient.invalidateQueries({ queryKey: ['gastos', 'stats'] });
      toast.success('Gasto registrado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al registrar el gasto');
    },
  });
}

export function useUpdateGasto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      gastosApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['gastos'] });
      queryClient.invalidateQueries({ queryKey: ['gastos', id] });
      queryClient.invalidateQueries({ queryKey: ['gastos', 'stats'] });
      toast.success('Gasto actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar el gasto');
    },
  });
}

export function useDeleteGasto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => gastosApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gastos'] });
      queryClient.invalidateQueries({ queryKey: ['gastos', 'stats'] });
      toast.success('Gasto eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el gasto');
    },
  });
}